import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Badge, Tooltip, Button, HStack, Icon, Text } from "@chakra-ui/react";
import { FiCopy } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { AgentType, agentTypeToJSON } from "../../build-ts/agent";
import { AgentConfig, AgentListResponseWrapper } from "../../build-ts/agent_config";
import { getAgentsList, duplicateAgents } from "services/EvaServices";
import Table, { TableColumn, TableFilter, TablePagination } from "../common/Table";
import { getAgentTypeColorForEva } from "utils/helperFuncUtils";


const AgentTable: React.FC = () => {
  const navigate = useNavigate();
  const hasMounted = useRef(false);

  // State for data
  const [agents, setAgents] = useState<AgentConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<TablePagination>({
    total: 0,
    offset: 0,
    has_more: false,
  });
  const [limit] = useState(50);

  // Agent filters
  const [filters, setFilters] = useState<Record<string, string>>({
    search: "",
    type: "all",
    active: "all",
  });
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Selection state
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());

  // Fetch agents from Darwin API
  const fetchAgents = useCallback(async (
      reset = false,
      currentSortBy = "name",
      currentSortOrder = "asc",
      currentFilters: Record<string, string> = filters,
      newOffset?: number
  ) => {
    try {
      if (reset) {
        setLoading(true);
      }
      setError(null);

      const currentOffset = newOffset !== undefined ? newOffset : (reset ? 0 : pagination.offset || 0);
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
        sort_by: currentSortBy,
        sort_order: currentSortOrder,
      });

      if (currentFilters.type && currentFilters.type !== "all") {
        params.append("agent_type", currentFilters.type);
      }
      if (currentFilters.search && currentFilters.search.trim()) {
        params.append("name", currentFilters.search);
      }
      if (currentFilters.active && currentFilters.active !== "all") {
        params.append("is_active", currentFilters.active);
      }

      const result: AgentListResponseWrapper = AgentListResponseWrapper.fromJSON(await getAgentsList(params));

      if (result.agents) {
        // Always replace the agents list, never append
        setAgents(result.agents as AgentConfig[]);

        if (result.pagination) {
          setPagination({
            total: result.pagination?.total || 0,
            has_more: result.pagination?.has_more || false,
            offset: currentOffset,
          });
        }
      } else {
        // API succeeded but no agents returned
        setAgents([]);
        setPagination({
          total: 0,
          has_more: false,
          offset: currentOffset,
        });
      }
    } catch (err) {
      const errorMsg = `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [limit, pagination.offset, filters]);

  // Load data on component mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      const loadData = async () => {
        await fetchAgents(true);
      };
      loadData();
    }
  }, [fetchAgents]);

  // Table event handlers
  const handlePageChange = useCallback(async (offset: number) => {
    await fetchAgents(false, sortBy, sortOrder, filters, offset);
  }, [fetchAgents, sortBy, sortOrder, filters]);

  const handleSort = useCallback(async (newSortBy: string, newSortOrder: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    await fetchAgents(true, newSortBy, newSortOrder, filters, 0);
  }, [fetchAgents, filters]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
  }, []);

  const handleApplyFilters = useCallback(async () => {
    await fetchAgents(true, sortBy, sortOrder, filters, 0);
  }, [fetchAgents, sortBy, sortOrder, filters]);

  const handleClearFilters = useCallback(async () => {
    const clearedFilters = { search: "", type: "all", active: "all" };
    setFilters(clearedFilters);
    setSortBy("name");
    setSortOrder("asc");
    await fetchAgents(true, "name", "asc", clearedFilters, 0);
  }, [fetchAgents]);

  const handleSelectionChange = useCallback((newSelectedAgents: Set<string>) => {
    setSelectedAgents(newSelectedAgents);
  }, []);

  // Get agent types from AgentType enum
  const agentTypes = useMemo(() => {
    return Object.values(AgentType).filter(
      value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_AGENT_TYPE"
    ) as string[];
  }, []);

  const handleAgentRowClick = (agent: AgentConfig) => {
    navigate(`/old-eva/${agent.id}`);
  };

  const handleDuplicateSelected = async () => {
    if (selectedAgents.size === 0) return;

    try {
      const agentIds = Array.from(selectedAgents);
      await duplicateAgents(agentIds);

      // Clear selection after successful duplication
      setSelectedAgents(new Set());

      // Refresh the agents list
      await fetchAgents(true, sortBy, sortOrder, filters, 0);
    } catch (error) {
      console.error("Error duplicating agents:", error);
    }
  };

  // Table configuration
  const tableColumns: TableColumn<AgentConfig>[] = useMemo(() => [
    {
      key: "name",
      label: "Agent Name",
      sortable: true,
      className: "eva-name-column",
      render: (agent) => (
        <Tooltip label={agent.name} placement="top">
          <Text fontWeight="semibold" noOfLines={1} wordBreak="break-word">
            {agent.name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "description",
      label: "Description",
      className: "eva-description-column",
      render: (agent) => (
        <Tooltip label={agent.description} placement="top">
          <Text fontSize="sm" color="gray.600" noOfLines={2} wordBreak="break-word">
            {agent.description || "No description available"}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "agent_type",
      label: "Type",
      className: "eva-type-column",
      render: (agent) => (
        <Badge colorScheme={getAgentTypeColorForEva(agent.agent_type)}>
          {agent.agent_type !== undefined ? agentTypeToJSON(agent.agent_type) : "Unknown"}
        </Badge>
      ),
    },
    {
      key: "version",
      label: "Version",
      className: "eva-version-column",
      render: (agent) => (
        <Badge colorScheme="purple" variant="outline">
          v{agent.version}
        </Badge>
      ),
    },
    {
      key: "is_active",
      label: "Status",
      className: "eva-status-column",
      render: (agent) => (
        <Badge colorScheme={agent.is_active ? "green" : "red"} variant="solid">
          {agent.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "prompt_config",
      label: "Model Config",
      className: "eva-model-column",
      render: (agent) => (
        <Text fontSize="sm" color="gray.600" wordBreak="break-word">
          {agent.prompt_config ? `${agent.prompt_config.model_name} (${agent.prompt_config.model_type})` : "No model config"}
        </Text>
      ),
    },
  ], []);

  const tableFilters: TableFilter[] = useMemo(() => [
    {
      key: "search",
      type: "text",
      label: "Search",
      placeholder: "Search agents...",
      width: "300px",
    },
    {
      key: "type",
      type: "select",
      label: "Type",
      options: [
        { value: "all", label: "All Types" },
        ...agentTypes.map(type => ({ value: type, label: type })),
      ],
      width: "200px",
    },
    {
      key: "active",
      type: "select",
      label: "Status",
      options: [
        { value: "all", label: "All Status" },
        { value: "true", label: "Active" },
        { value: "false", label: "Inactive" },
      ],
      width: "150px",
    },
  ], [agentTypes]);

  const headerActions = selectedAgents.size > 0 ? (
    <HStack spacing={4}>
      <Button
        onClick={handleDuplicateSelected}
        colorScheme="blue"
        size="sm"
        leftIcon={<Icon as={FiCopy} />}
      >
        Duplicate
      </Button>
      <Button
        onClick={() => setSelectedAgents(new Set())}
        variant="outline"
        size="sm"
      >
        Cancel
      </Button>
    </HStack>
  ) : null;

  return (
    <Table
      data={agents}
      columns={tableColumns}
      loading={loading}
      error={error}
      pagination={pagination}
      limit={limit}
      onPageChange={handlePageChange}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={handleSort}
      filters={tableFilters}
      onFilterChange={handleFilterChange}
      onApplyFilters={handleApplyFilters}
      onClearFilters={handleClearFilters}
      selectable={true}
      selectedItems={selectedAgents}
      onSelectionChange={handleSelectionChange}
      getItemId={(agent) => agent.id || ""}
      onRowClick={handleAgentRowClick}
      headerActions={headerActions}
      emptyMessage="No agents found"
      emptySubMessage="Try adjusting your search criteria or create a new agent"
    />
  );
};

export default AgentTable;
